import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Features', path: '/features' },
    { name: 'Templates', path: '/templates' },
    { name: 'Plugins', path: '/plugins' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (showSignIn) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showSignIn]);

  const showWhiteBg = !transparent || isScrolled;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggedIn(true);
    setShowSignIn(false);
    navigate('/dashboard');
  };

  const handleSignOut = () => {
    setIsLoggedIn(false);
    navigate('/');
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showWhiteBg ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className={`text-2xl font-bold transition-all duration-500 cursor-pointer ${showWhiteBg ? 'text-gray-900' : 'text-white'} ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
          >
            Your App Wizard
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`transition-all duration-500 cursor-pointer font-medium text-sm hover:scale-105 ${
                  location.pathname === item.path
                    ? 'text-cyan-600'
                    : showWhiteBg
                      ? 'text-gray-700 hover:text-cyan-600'
                      : 'text-white/90 hover:text-white'
                } ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
                style={{ transitionDelay: `${(index + 1) * 100}ms` }}
              >
                {item.name}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <Link
                  to="/dashboard"
                  className={`px-5 py-2.5 rounded-lg border font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 flex items-center gap-2 ${
                    showWhiteBg
                      ? 'border-gray-300 text-gray-700 hover:border-cyan-500 hover:text-cyan-600'
                      : 'border-white/40 text-white hover:border-white hover:bg-white/10'
                  }`}
                >
                  <i className="ri-dashboard-line"></i>
                  My Dashboard
                </Link>
                <button
                  onClick={handleSignOut}
                  className={`px-5 py-2.5 rounded-lg border font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 ${
                    showWhiteBg
                      ? 'border-gray-300 text-gray-700 hover:border-red-400 hover:text-red-500'
                      : 'border-white/40 text-white hover:border-white hover:bg-white/10'
                  }`}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { setIsSignUp(false); setShowSignIn(true); }}
                  className={`px-5 py-2.5 rounded-lg border font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 ${
                    showWhiteBg
                      ? 'border-gray-300 text-gray-700 hover:border-cyan-500 hover:text-cyan-600'
                      : 'border-white/40 text-white hover:border-white hover:bg-white/10'
                  } ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                  style={{ transitionDelay: '450ms' }}
                >
                  Sign In
                </button>
                <Link
                  to="/get-started"
                  className={`px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-lg hover:from-cyan-600 hover:to-teal-600 transition-all duration-500 whitespace-nowrap cursor-pointer font-medium text-sm shadow-lg shadow-cyan-500/30 hover:scale-105 hover:shadow-xl ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
                  style={{ transitionDelay: '500ms' }}
                >
                  Start Building Free
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Sign In Modal */}
      {showSignIn && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSignIn(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-[fadeInUp_0.3s_ease]">
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 px-8 py-8 text-white text-center relative">
              <button
                onClick={() => setShowSignIn(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="text-3xl font-bold mb-1">Your App Wizard</div>
              <p className="text-white/80 text-sm">{isSignUp ? 'Create your account' : 'Welcome back'}</p>
            </div>

            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setIsSignUp(false)}
                className={`flex-1 py-3.5 text-sm font-semibold transition-colors cursor-pointer ${!isSignUp ? 'text-cyan-600 border-b-2 border-cyan-500' : 'text-gray-400 hover:text-gray-600'}`}
              >
                Sign In
              </button>
              <button
                onClick={() => setIsSignUp(true)}
                className={`flex-1 py-3.5 text-sm font-semibold transition-colors cursor-pointer ${isSignUp ? 'text-cyan-600 border-b-2 border-cyan-500' : 'text-gray-400 hover:text-gray-600'}`}
              >
                Create Account
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-8 py-7 space-y-4">
              {isSignUp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-5 h-5 flex items-center justify-center"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>
              {!isSignUp && (
                <div className="text-right">
                  <a href="#" className="text-xs text-cyan-600 hover:underline cursor-pointer">Forgot password?</a>
                </div>
              )}
              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-lg font-semibold text-sm hover:from-cyan-600 hover:to-teal-600 transition-all shadow-lg shadow-cyan-500/30 hover:scale-[1.02] cursor-pointer whitespace-nowrap mt-2"
              >
                {isSignUp ? 'Create Account' : 'Sign In to Your App Wizard'}
              </button>

              <div className="relative flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-gray-100"></div>
                <span className="text-xs text-gray-400">or continue with</span>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={handleSubmit} className="flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition cursor-pointer whitespace-nowrap">
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
                  Google
                </button>
                <button type="button" onClick={handleSubmit} className="flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition cursor-pointer whitespace-nowrap">
                  <i className="ri-github-fill text-gray-800 text-base"></i>
                  GitHub
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
